import React from 'react';
import type { FunctionComponent, ReactElement } from 'react';
import { spacingHelper, Inline } from '@construct-kit/core';
import styled, { css } from 'styled-components';
import { StyledContainer, Grid } from './layouts';
import FiltersContainer from './components/FiltersContainer';
import type { SelectedFiltersOptionsType } from '../../../shared/model/constants/filterTypes';
import type { ChannelObject } from '../../../shared/model/tsTypes';
import { LocationSearchBar } from '../../../shared/components/LocationSearchBar';
import { trackSearchRefinementModalOpen } from '../../../shared/tracking/tealium/tracking';
import type { MapViewPropType } from '../model/query/mapViewOptions';

const FiltersWrapper = styled(Inline).attrs({ gap: '0' })`
  overflow-x: visible;
  white-space: nowrap;

  // For Chrome and Safari
  &::-webkit-scrollbar {
    -webkit-appearance: none;
    display: none;
  }
  scrollbar-width: none; // For Firefox
  padding-top: ${spacingHelper('small')};

  ${({ theme }) => css`
    ${theme.layoutBreakpoints.extraLarge} {
      padding-top: 0;
    }
  `}

  > *:first-child {
    ${({ theme }) => css`
      ${theme.layoutBreakpoints.extraLarge} {
          margin-right: ${spacingHelper('medium')};
    `}
  }
`;

const AdditionalElementsContainer = styled.div`
  justify-content: flex-end;
  display: flex;
  flex-grow: 0;
`;

const LocationSearchWrapper = styled.div`
  position: relative;

  ${({ theme }) => css`
    ${theme.layoutBreakpoints.extraLarge} {
      width: 22.5rem;
  `}
`;

export type SearchBarProps = {
  onFilterOpen: () => void;
  selectedFiltersOptions: SelectedFiltersOptionsType;
  initialFilterOptions: SelectedFiltersOptionsType;
  additionalElements: ReactElement[];
  setSelectedFiltersOptions: (value: SelectedFiltersOptionsType) => void;
  handleSearchOnClick: (onClose?: () => void) => void;
  handleChannelChange: (channel: ChannelObject) => void;
  mapView?: MapViewPropType;
};

const SearchBar: FunctionComponent<SearchBarProps> = ({
  onFilterOpen,
  selectedFiltersOptions,
  initialFilterOptions,
  additionalElements,
  setSelectedFiltersOptions,
  handleSearchOnClick,
  handleChannelChange,
  mapView,
}) => {
  const onLocationSearchBoxClick = () => {
    trackSearchRefinementModalOpen({
      searchMode: mapView ? 'map' : 'list',
      userAction: 'location-field',
    });
  };

  return (
    <StyledContainer>
      <Grid fullWidth={!!mapView}>
        <LocationSearchWrapper>
          <LocationSearchBar
            selectedFiltersOptions={selectedFiltersOptions}
            initialFilterOptions={initialFilterOptions}
            setSelectedFiltersOptions={setSelectedFiltersOptions}
            handleSearchOnClick={handleSearchOnClick}
            onLocationSearchBoxClick={onLocationSearchBoxClick}
          />
        </LocationSearchWrapper>
        <FiltersWrapper>
          <FiltersContainer
            selectedFiltersOptions={selectedFiltersOptions}
            initialFilterOptions={initialFilterOptions}
            setSelectedFiltersOptions={setSelectedFiltersOptions}
            onFilterOpen={onFilterOpen}
            handleSearchOnClick={handleSearchOnClick}
            handleChannelChange={handleChannelChange}
            mapView={mapView}
          />
          <AdditionalElementsContainer>{additionalElements}</AdditionalElementsContainer>
        </FiltersWrapper>
      </Grid>
    </StyledContainer>
  );
};

export default SearchBar;
